import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { getPublicSurvey, submitPublicSurvey } from "../../api/api";
import SurveyRunner from "../survey/SurveyRunner";
import { PUBLIC_FEEDBACK_SURVEY_ID } from "../survey/surveyUtils";

/**
 * Unauthenticated feedback form: name, email, and a message.
 *
 * @param {Object} props
 * @param {number} [props.userId]
 * @param {object} [props.user]
 * @returns {JSX.Element}
 */
export default function PublicFeedbackPage({ userId, user }) {
  const navigate = useNavigate();
  const [definition, setDefinition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const isPersonalUser = Boolean(userId) && user?.type !== "organization";

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (isPersonalUser) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setError("");
      const result = await getPublicSurvey(PUBLIC_FEEDBACK_SURVEY_ID);
      if (!mounted) return;
      if (result.error) {
        setError(result.error);
        setDefinition(null);
      } else {
        setDefinition(result.data?.definition || null);
      }
      setLoading(false);
    }
    load();
    return () => {
      mounted = false;
    };
  }, [isPersonalUser]);

  if (isPersonalUser) {
    return <Navigate to="/beta" replace />;
  }

  /**
   * @param {Record<string, unknown>} answers
   * @param {string} pageId
   * @returns {Promise<Object>}
   */
  async function handleSubmit(answers, pageId) {
    return submitPublicSurvey(PUBLIC_FEEDBACK_SURVEY_ID, answers, pageId);
  }

  return (
    <div className="bg-ui-background min-h-[calc(100dvh-3.5rem)] w-full">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
        {loading ? <p className="text-sm text-text-muted">Loading…</p> : null}
        {error ? (
          <p className="text-sm text-error" role="alert">
            {error}
          </p>
        ) : null}
        {definition ? (
          <>
            <h1 className="font-display text-3xl font-medium text-text-primary">
              {definition.title}
            </h1>
            {definition.intro?.body ? (
              <p className="mt-2 text-text-secondary">{definition.intro.body}</p>
            ) : null}
            <div className="mt-6">
              <SurveyRunner
                definition={definition}
                mode="draft"
                onSubmit={handleSubmit}
                onDone={() => navigate("/feedback/thanks", { replace: true })}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
